import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import { useCustomToast } from '@/shared/lib';
import { getDashboard } from '../api/dashboard.api';
import type { DashboardData } from './dashboard.types';
import { getComparisonLabel, getCurrentPeriod, type Granularity } from './period';

export const DASHBOARD_LOAD_ERROR_MESSAGE = 'Erro ao carregar o dashboard, tente novamente.';

export const useDashboardStore = defineStore('dashboard', () => {
  const toast = useCustomToast();
  const granularity = ref<Granularity>('monthly');
  const period = ref<string>(getCurrentPeriod('monthly'));
  const data = ref<DashboardData | null>(null);
  const isLoading = ref(true);
  const error = ref<string | null>(null);

  const comparisonLabel = computed(() => getComparisonLabel(granularity.value));

  let latestRequestId = 0;

  async function fetchDashboard(): Promise<void> {
    const requestId = ++latestRequestId;
    try {
      isLoading.value = true;
      error.value = null;
      const response = await getDashboard(granularity.value, period.value);
      if (requestId !== latestRequestId) return;
      data.value = response;
    } catch {
      if (requestId !== latestRequestId) return;
      error.value = DASHBOARD_LOAD_ERROR_MESSAGE;
      data.value = null;
      toast.showErrorToast({ title: DASHBOARD_LOAD_ERROR_MESSAGE, type: 'error' });
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  }

  async function setGranularity(nextGranularity: Granularity): Promise<void> {
    if (nextGranularity === granularity.value) return;
    granularity.value = nextGranularity;
    period.value = getCurrentPeriod(nextGranularity);
    await fetchDashboard();
  }

  async function setPeriod(nextPeriod: string): Promise<void> {
    if (nextPeriod === period.value) return;
    period.value = nextPeriod;
    await fetchDashboard();
  }

  function reset(): void {
    latestRequestId += 1;
    data.value = null;
    error.value = null;
    isLoading.value = true;
  }

  return {
    granularity: readonly(granularity),
    period: readonly(period),
    data: readonly(data),
    isLoading: readonly(isLoading),
    error: readonly(error),
    comparisonLabel,
    fetchDashboard,
    setGranularity,
    setPeriod,
    reset,
  };
});
